/**
 * AppSidebar — the logged-in left navigation sidebar (Strishakti-style).
 *
 * Pure navigation: icon + label items, grouped into sections, with an active
 * state for the current route. No widget cards (stats/health-tips/quick-links
 * were removed — they were hardcoded mock data). Rendered both as the fixed
 * desktop sidebar and inside the mobile slide-in drawer by `GovLayout`.
 *
 * Labels resolve from `nav.*` i18n keys (EN/HI/MR). The Administration section is
 * shown only to ADMIN / SUPER_ADMIN. Items are keyed by path (unique) so two
 * items may share an i18n key (e.g. "competitions" → /competitions and
 * /admin/competitions) without React key collisions.
 */
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { SIDEBAR_SECTIONS, resolvePath, type AppNavSection } from '@/config/appNav'
import { cn } from '@/lib/utils'

interface AppSidebarProps {
  /** called after a nav link is clicked (used to close the mobile drawer) */
  onNavigate?: () => void
}

export function AppSidebar({ onNavigate }: AppSidebarProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const location = useLocation()

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  const renderSection = (section: AppNavSection) => {
    // Skip admin-only sections for non-admins.
    if (section.adminOnly && !isAdmin) return null
    // Hide user-facing sections (e.g. Secondary destinations) from admins so
    // their sidebar shows only the clean Administration section.
    if (section.hideForAdmin && isAdmin) return null
    // Skip volunteer-only sections for non-volunteers.
    if (section.volunteerOnly && user?.userType !== 'VOLUNTEER_COMMUNITY') return null

    return (
      <div
        key={section.titleKey ?? 'main'}
        className={cn('flex flex-col gap-0.5', section.bottom && 'mt-auto pt-3 border-t border-gray-100')}
      >
        {section.titleKey && (
          <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {t(section.titleKey)}
          </p>
        )}
        {section.items.map((item) => {
          const path = resolvePath(item, user?.username)
          const Icon = item.icon
          const active = isActive(path)
          return (
            <Link
              key={path}
              to={path}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[40px]',
                active
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{t(`nav.${item.key}`, item.label)}</span>
            </Link>
          )
        })}
      </div>
    )
  }

  return (
    <nav aria-label="Primary" className="flex flex-col h-full gap-1 px-3 py-3 overflow-y-auto">
      {SIDEBAR_SECTIONS.map(renderSection)}
    </nav>
  )
}

export default AppSidebar